import {css} from "@panda/css";

import {CenterBox} from "@pg-design/grid";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {hitGTMEcommerceRemoveFromCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_remove_from_cart";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmStepApplicationClose} from "../../tracking/google_tag_manager/gtm_event_step_application_close";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {getPreferencesFromLeadData} from "../../user/utils/preferences/get_preferences_from_lead_data";
import {appendMultiLeadApplicationsTypes} from "../actions/multi_lead/append_multi_lead_applications";
import {getVendorRecommendations} from "../api/multi_lead/get_vendor_recommendations";
import {initialEmptyApplicationValues} from "../constants/form/application_form_fields";
import {leadModalZindex} from "../constants/lead/lead_modal_zindex";
import {vendorRecommendationsDistanceInKm} from "../constants/multi_lead/application_multi_lead_constants";
import {useMultiLeadRecommendations} from "../hooks/multi_lead/use_application_multi_lead_recommendations";
import {useApplicationModalArray} from "../hooks/use_application_form_modal_array";
import {useLeadRequiredParams} from "../hooks/use_lead_required_params";
import {ModalStepActionTypes} from "../reducers/application_modal_step_reducer";
import {hitMultileadRecommendationShown} from "../tracking/lead/hit_multilead_recommendation_shown";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationNotificationConsentStepPayload} from "../types/IApplicationNotificationConsentStepPayload";
import {IApplicationOriginalProperty} from "../types/IApplicationOriginalProperty";
import {IApplicationPreferencesSetStepPayload} from "../types/IApplicationPreferencesSetStepPayload";
import {MultileadVersion} from "../types/lead/MultileadVersion";
import {RecommendationSource} from "../types/lead/RecommendationSource";
import {ApplicationMultiLeadRecommendationType} from "../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {IApplicationMultiLeadSetSummaryStepPayload} from "../types/multi_lead/IApplicationMultiLeadSetSummaryStepPayload";
import {IApplicationMultiLeadStepPayload} from "../types/multi_lead/IApplicationMultiLeadStepPayload";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSource, ApplicationSourceSection} from "../utils/form/application_form_source";
import {getSentLeadFormValuesFromStorage} from "../utils/lead/sent_lead_form_values";
import {report} from "../utils/multi_lead/application_multi_lead_analytics_events";
import {fetchApplicationMultiLeadSimpleRecommendations} from "../utils/multi_lead/fetch_application_multi_lead_simple_recommendations";
import {getStorageSentApplicationsIdListByOffer} from "../utils/multi_lead/get_storage_sent_applications_id_list_by_offer";
import {ApplicationGoodbyeContentModal} from "./goodbye_content_modal/ApplicationGoodbyeContentModal";
import {LeadModalAskForFullOfferForm} from "./lead_modal_form/lead_modal_ask_for_full_offer_form/LeadModalAskForFullOfferForm";
import {LeadModalNegotiationForm} from "./lead_modal_form/lead_modal_negotiation_form/LeadModalNegotiationForm";
import {LeadModalTopicForm} from "./lead_modal_form/lead_modal_topic_form/LeadModalTopicForm";
import {ApplicationMultiLead} from "./multi_lead/ApplicationMultiLead";
import {ApplicationNotificationConsentConfirmationModal} from "./notification_consent_confirmation_modal/ApplicationNotificationConsentConfirmationModal";
import {ApplicationSuccessInfoModal} from "./success_info_modal/ApplicationSuccessInfoModal";
import {LeadModalUnifiedForm} from "./unified_lead_form/LeadModalUnifiedForm";

export const ApplicationModalArray = () => {
    const dispatch = useAppDispatch();
    const {offerId, propertyId} = useLeadRequiredParams();

    const {modalStepState, dispatchModalStepState, isOpened, source, sourceSection, parametrizedStoreHash, initialLeadFormValues, leadData} =
        useApplicationModalArray();

    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    // TODO: 3 props below are only user in LeadGeneratorSuccessInfo scenario, create a new component for this scenario
    const customSourceSection = useAppSelector((state) => state.mainLead.customSourceSection);
    const preventClosingWhenNoData = useAppSelector((state) => state.mainLead.preventClosingWhenNoData);
    const roomCount = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.rooms) as number;
    const propertyPrice = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.price) as number | null;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const currentViewType = useAppSelector((store: IRPStore) => store.viewType.current);
    const recommendationType = propertyId ? ApplicationMultiLeadRecommendationType.PROPERTY : ApplicationMultiLeadRecommendationType.OFFER;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = useAppSelector((state) => state.application[parametrizedStoreHash]?.application) || null;

    const {areRecommendationsEmpty, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId,
        offerId
    });

    const onModalClose = () => {
        if (modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD && mainLeadType) {
            gtmStepApplicationClose(mainLeadType);
        }

        const shouldTrackRemoveFromCart =
            (modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ||
                modalStepState.step === ApplicationModalStep.APPLICATION_FORM_FULL_OFFER ||
                modalStepState.step === ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION) &&
            leadData &&
            mainLeadType;

        if (shouldTrackRemoveFromCart) {
            hitGTMEcommerceRemoveFromCart({
                data: leadData,
                viewType: currentViewType,
                leadType: mainLeadType,
                sourceSection: customSourceSection || sourceSection
            });
        }

        dispatchModalStepState({type: ModalStepActionTypes.CLOSE});
    };

    const setNotificationConsentStep = (payload: IApplicationNotificationConsentStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP,
            payload
        });
    };

    const setAfterMultiLeadStep = (payload: IApplicationMultiLeadSummaryStepState) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload
        });
    };

    const setMultiLeadStep = (payload: IApplicationMultiLeadStepPayload) => {
        if (!isMultileadStepEnabled) {
            return;
        }

        dispatchModalStepState({
            type: ModalStepActionTypes.SET_MULTILEAD_STEP,
            payload
        });
    };

    const setModalClose = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_CLOSE
        });
    };

    // TODO: Needs to be cleaned up, after preferences step is no more shown in this flow.
    const setPreferencesStepWithCountryCheck = (payload: IApplicationPreferencesSetStepPayload) => {
        const showNotificationConsent = !payload.originalApplicationResponse?.marketing_email || !payload.originalApplicationResponse?.marketing_sms;

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const updateMainLeadResponse = (sentApplication: IApplicationData | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA,
            payload: {
                originalApplicationResponse: sentApplication
            }
        });
    };

    const handleCloseMultiLead = (_payload?: IApplicationMultiLeadSetSummaryStepPayload) => {
        const startedAtMultilead = initialStep === ApplicationModalStep.MULTILEAD;
        if (isMultileadStepEnabled || startedAtMultilead) {
            report.modalClose(recommendationType, currentViewType);
        }

        dispatch({type: appendMultiLeadApplicationsTypes.reset, id: parametrizedStoreHash});
        setModalClose();
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const onSingleLeadSuccess = async (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        updateMainLeadResponse(sentApplication);

        if (leadData && leadData.offer?.detail?.region.country === Country.POLAND) {
            putPreferences(getPreferencesFromLeadData(leadData));
        }

        let recommendedList: IRecommendedProperty[] | IRecommendedOffer[] | null = null;
        let multiLeadProperty: IApplicationOriginalProperty | null = property;
        const isAbroadLead = leadData?.offer?.detail?.region.country !== Country.POLAND;

        // Fetch recommendations only if multilead is enabled
        if (isMultileadStepEnabled && !isAbroadLead) {
            recommendedList = await fetchApplicationMultiLeadSimpleRecommendations({
                dispatch,
                offer,
                property,
                sentApplication,
                recommendedFilters
            });
        }

        if (!isAbroadLead) {
            const vendorRecommendations = await getVendorRecommendations(
                {
                    offerId: offer?.id,
                    originalApplicationProperty: property,
                    excludedOfferIds: getStorageSentApplicationsIdListByOffer(offer, {limit: 50, distance: vendorRecommendationsDistanceInKm * 1000}),
                    radius: vendorRecommendationsDistanceInKm
                },
                dispatch
            );

            recommendedList = vendorRecommendations.results;
            multiLeadProperty = vendorRecommendations.leadProperty;
        }

        if (isMultileadStepEnabled && (!recommendedList || recommendedList.length === 0)) {
            const recommendationsType = property ? ApplicationMultiLeadRecommendationType.PROPERTY : ApplicationMultiLeadRecommendationType.OFFER;

            hitMultileadRecommendationShown({
                recommendationType: isAbroadLead ? recommendationsType : ApplicationMultiLeadRecommendationType.VENDOR,
                recommendationId: null,
                viewType: currentViewType,
                recommendationSource: recommendationSource,
                multileadVersion:
                    recommendationsType === ApplicationMultiLeadRecommendationType.PROPERTY ? MultileadVersion.RP_LIST_PROPERTY : MultileadVersion.RP_LIST_OFFER
            });
        }

        const originalApplication = sentApplication ? sentApplication : null;
        const isWithOriginalApplicationResponse = Boolean(originalApplication);
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;
        const isWithRecommendations = recommendedList && recommendedList.length > 0;

        if (isMultileadStepEnabled && isWithOriginalApplicationResponse && isWithRecommendations) {
            setMultiLeadStep({
                originalApplicationResponse: originalApplication,
                recommendedOffers: recommendedList,
                shouldCloseOnOverlayClick: false,
                multiLeadProperty,
                recommendationsWithPriceFilter
            });
            return;
        }

        const isMarketingOptInSelected = sentApplication?.marketing_email || sentApplication?.marketing_sms;
        if (isWithOriginalApplicationResponse && !isMarketingOptInSelected) {
            setNotificationConsentStep({
                originalApplicationResponse: originalApplication
            });
            return;
        }

        setModalClose();
        return;
    };

    // Render
    const renderContent = () => {
        // render cases
        switch (modalStepState.step) {
            case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
                return initialLeadFormValues ? (
                    <LeadModalUnifiedForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                ) : null;
            }
            case ApplicationModalStep.APPLICATION_FORM_FULL_OFFER: {
                return (
                    <LeadModalAskForFullOfferForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return (
                    <LeadModalNegotiationForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                        price={propertyPrice}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return (
                    <LeadModalTopicForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case ApplicationModalStep.NOTIFICATION_CONSENT: {
                return (
                    <ApplicationNotificationConsentConfirmationModal
                        storeHash={parametrizedStoreHash}
                        result={modalStepState.originalApplicationResponse}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        onModalClose={onModalClose}
                        sourceSection={sourceSection}
                    />
                );
            }
            case ApplicationModalStep.SUCCESS_INFO: {
                return (
                    <ApplicationSuccessInfoModal
                        isModal
                        modalStepState={modalStepState}
                        onClick={() =>
                            dispatchModalStepState({
                                type: ModalStepActionTypes.NEXT_STEP
                            })
                        }
                        shouldRenderRecommendations
                        onModalClose={onModalClose}
                    />
                );
            }
            case ApplicationModalStep.GOODBYE_INFO: {
                return (
                    <ApplicationGoodbyeContentModal storeHash={parametrizedStoreHash} source={source} sourceSection={sourceSection} closeModal={onModalClose} />
                );
            }
            case ApplicationModalStep.MULTILEAD: {
                if ((!isMultileadStepEnabled && initialStep === ApplicationModalStep.MULTILEAD) || areRecommendationsLoading) {
                    return (
                        <div className={multiLeadWrapperStyle}>
                            <CenterBox>
                                <Loader />
                            </CenterBox>
                        </div>
                    );
                }
                if (!isMultileadStepEnabled) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return null;
                }

                const sentLeadFormValues = getSentLeadFormValuesFromStorage();
                if (!sentLeadFormValues && !preventClosingWhenNoData) {
                    // application data should be filled at this point. But just in case:
                    onModalClose();
                    return null;
                }

                const multiLeadOriginalApplication = modalStepState.originalApplicationResponse || originalApplication || null;

                if (areRecommendationsEmpty) {
                    setPreferencesStepWithCountryCheck({
                        originalApplicationResponse: multiLeadOriginalApplication,
                        multiLeadSummaryItems: [],
                        propertyId,
                        offerId
                    });
                }

                const originalApplicationFormValues = sentLeadFormValues
                    ? {...sentLeadFormValues, phone: getPhoneDataFromString(sentLeadFormValues.phone)}
                    : {...initialEmptyApplicationValues, rooms: [roomCount]};

                return (
                    <div className={multiLeadWrapperStyle}>
                        <ApplicationMultiLead
                            offerId={offerId || undefined}
                            propertyId={propertyId || undefined}
                            originalApplicationResponse={multiLeadOriginalApplication}
                            originalApplicationFormValues={originalApplicationFormValues}
                            storeHash={parametrizedStoreHash}
                            sourceSection={ApplicationSourceSection.MULTILEAD}
                            recommendationType={recommendationType}
                            recommendationsWithPriceFilter={modalStepState.recommendationsWithPriceFilter}
                            customSourceSection={customSourceSection || undefined}
                            handleModalClose={handleCloseMultiLead}
                            handleSetMultiLeadSummaryStep={setAfterMultiLeadStep}
                        />
                    </div>
                );
            }
            case ApplicationModalStep.WAITING:
            default:
                return (
                    <div className={waitingWrapperStyle}>
                        <Loader size="lg" fullWidth />
                    </div>
                );
        }
    };

    const shouldCloseOnOverlayClick = modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ? false : modalStepState.shouldCloseOnOverlayClick;

    return (
        <Modal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            zIndex={leadModalZindex}
            showCloseButton={false}
            fullScreen="xs"
        >
            {modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ? (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderStyle}>{renderContent()}</div>
                </div>
            ) : (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderFullStyle}>{renderContent()}</div>
                </div>
            )}
        </Modal>
    );
};

const applicationWrapStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%"
});

const applicationHolderStyle = css({
    width: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const applicationHolderFullStyle = css({
    width: "100%",
    height: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const multiLeadWrapperStyle = css({
    height: "100%",
    md: {
        width: "950px",
        height: "auto"
    }
});

const waitingWrapperStyle = css({
    position: "relative",
    md: {
        width: "800px",
        height: "auto"
    }
});
import {css} from "@panda/css";

import {CenterBox} from "@pg-design/grid";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {hitGTMEcommerceRemoveFromCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_remove_from_cart";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmStepApplicationClose} from "../../tracking/google_tag_manager/gtm_event_step_application_close";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {getPreferencesFromLeadData} from "../../user/utils/preferences/get_preferences_from_lead_data";
import {appendMultiLeadApplicationsTypes} from "../actions/multi_lead/append_multi_lead_applications";
import {getVendorRecommendations} from "../api/multi_lead/get_vendor_recommendations";
import {initialEmptyApplicationValues} from "../constants/form/application_form_fields";
import {leadModalZindex} from "../constants/lead/lead_modal_zindex";
import {vendorRecommendationsDistanceInKm} from "../constants/multi_lead/application_multi_lead_constants";
import {useMultiLeadRecommendations} from "../hooks/multi_lead/use_application_multi_lead_recommendations";
import {useApplicationModalArray} from "../hooks/use_application_form_modal_array";
import {useLeadRequiredParams} from "../hooks/use_lead_required_params";
import {ModalStepActionTypes} from "../reducers/application_modal_step_reducer";
import {hitMultileadRecommendationShown} from "../tracking/lead/hit_multilead_recommendation_shown";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationNotificationConsentStepPayload} from "../types/IApplicationNotificationConsentStepPayload";
import {IApplicationOriginalProperty} from "../types/IApplicationOriginalProperty";
import {IApplicationPreferencesSetStepPayload} from "../types/IApplicationPreferencesSetStepPayload";
import {MultileadVersion} from "../types/lead/MultileadVersion";
import {RecommendationSource} from "../types/lead/RecommendationSource";
import {ApplicationMultiLeadRecommendationType} from "../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {IApplicationMultiLeadSetSummaryStepPayload} from "../types/multi_lead/IApplicationMultiLeadSetSummaryStepPayload";
import {IApplicationMultiLeadStepPayload} from "../types/multi_lead/IApplicationMultiLeadStepPayload";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSource, ApplicationSourceSection} from "../utils/form/application_form_source";
import {getSentLeadFormValuesFromStorage} from "../utils/lead/sent_lead_form_values";
import {report} from "../utils/multi_lead/application_multi_lead_analytics_events";
import {fetchApplicationMultiLeadSimpleRecommendations} from "../utils/multi_lead/fetch_application_multi_lead_simple_recommendations";
import {getStorageSentApplicationsIdListByOffer} from "../utils/multi_lead/get_storage_sent_applications_id_list_by_offer";
import {ApplicationGoodbyeContentModal} from "./goodbye_content_modal/ApplicationGoodbyeContentModal";
import {LeadModalAskForFullOfferForm} from "./lead_modal_form/lead_modal_ask_for_full_offer_form/LeadModalAskForFullOfferForm";
import {LeadModalNegotiationForm} from "./lead_modal_form/lead_modal_negotiation_form/LeadModalNegotiationForm";
import {LeadModalTopicForm} from "./lead_modal_form/lead_modal_topic_form/LeadModalTopicForm";
import {ApplicationMultiLead} from "./multi_lead/ApplicationMultiLead";
import {ApplicationNotificationConsentConfirmationModal} from "./notification_consent_confirmation_modal/ApplicationNotificationConsentConfirmationModal";
import {ApplicationSuccessInfoModal} from "./success_info_modal/ApplicationSuccessInfoModal";
import {LeadModalUnifiedForm} from "./unified_lead_form/LeadModalUnifiedForm";

export const ApplicationModalArray = () => {
    const dispatch = useAppDispatch();
    const {offerId, propertyId} = useLeadRequiredParams();

    const {modalStepState, dispatchModalStepState, isOpened, source, sourceSection, parametrizedStoreHash, initialLeadFormValues, leadData} =
        useApplicationModalArray();

    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    // TODO: 3 props below are only user in LeadGeneratorSuccessInfo scenario, create a new component for this scenario
    const customSourceSection = useAppSelector((state) => state.mainLead.customSourceSection);
    const preventClosingWhenNoData = useAppSelector((state) => state.mainLead.preventClosingWhenNoData);
    const roomCount = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.rooms) as number;
    const propertyPrice = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.price) as number | null;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const currentViewType = useAppSelector((store: IRPStore) => store.viewType.current);
    const recommendationType = propertyId ? ApplicationMultiLeadRecommendationType.PROPERTY : ApplicationMultiLeadRecommendationType.OFFER;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = useAppSelector((state) => state.application[parametrizedStoreHash]?.application) || null;

    const {areRecommendationsEmpty, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId,
        offerId
    });

    const onModalClose = () => {
        if (modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD && mainLeadType) {
            gtmStepApplicationClose(mainLeadType);
        }

        const shouldTrackRemoveFromCart =
            (modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ||
                modalStepState.step === ApplicationModalStep.APPLICATION_FORM_FULL_OFFER ||
                modalStepState.step === ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION) &&
            leadData &&
            mainLeadType;

        if (shouldTrackRemoveFromCart) {
            hitGTMEcommerceRemoveFromCart({
                data: leadData,
                viewType: currentViewType,
                leadType: mainLeadType,
                sourceSection: customSourceSection || sourceSection
            });
        }

        dispatchModalStepState({type: ModalStepActionTypes.CLOSE});
    };

    const setNotificationConsentStep = (payload: IApplicationNotificationConsentStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP,
            payload
        });
    };

    const setAfterMultiLeadStep = (payload: IApplicationMultiLeadSummaryStepState) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload
        });
    };

    const setMultiLeadStep = (payload: IApplicationMultiLeadStepPayload) => {
        if (!isMultileadStepEnabled) {
            return;
        }

        dispatchModalStepState({
            type: ModalStepActionTypes.SET_MULTILEAD_STEP,
            payload
        });
    };

    const setModalClose = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_CLOSE
        });
    };

    // TODO: Needs to be cleaned up, after preferences step is no more shown in this flow.
    const setPreferencesStepWithCountryCheck = (payload: IApplicationPreferencesSetStepPayload) => {
        const showNotificationConsent = !payload.originalApplicationResponse?.marketing_email || !payload.originalApplicationResponse?.marketing_sms;

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const updateMainLeadResponse = (sentApplication: IApplicationData | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA,
            payload: {
                originalApplicationResponse: sentApplication
            }
        });
    };

    const handleCloseMultiLead = (_payload?: IApplicationMultiLeadSetSummaryStepPayload) => {
        const startedAtMultilead = initialStep === ApplicationModalStep.MULTILEAD;
        if (isMultileadStepEnabled || startedAtMultilead) {
            report.modalClose(recommendationType, currentViewType);
        }

        dispatch({type: appendMultiLeadApplicationsTypes.reset, id: parametrizedStoreHash});
        setModalClose();
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const onSingleLeadSuccess = async (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        updateMainLeadResponse(sentApplication);

        if (leadData && leadData.offer?.detail?.region.country === Country.POLAND) {
            putPreferences(getPreferencesFromLeadData(leadData));
        }

        let recommendedList: IRecommendedProperty[] | IRecommendedOffer[] | null = null;
        let multiLeadProperty: IApplicationOriginalProperty | null = property;
        const isAbroadLead = leadData?.offer?.detail?.region.country !== Country.POLAND;

        // Fetch recommendations only if multilead is enabled
        if (isMultileadStepEnabled && !isAbroadLead) {
            recommendedList = await fetchApplicationMultiLeadSimpleRecommendations({
                dispatch,
                offer,
                property,
                sentApplication,
                recommendedFilters
            });
        }

        if (!isAbroadLead) {
            const vendorRecommendations = await getVendorRecommendations(
                {
                    offerId: offer?.id,
                    originalApplicationProperty: property,
                    excludedOfferIds: getStorageSentApplicationsIdListByOffer(offer, {limit: 50, distance: vendorRecommendationsDistanceInKm * 1000}),
                    radius: vendorRecommendationsDistanceInKm
                },
                dispatch
            );

            recommendedList = vendorRecommendations.results;
            multiLeadProperty = vendorRecommendations.leadProperty;
        }

        if (isMultileadStepEnabled && (!recommendedList || recommendedList.length === 0)) {
            const recommendationsType = property ? ApplicationMultiLeadRecommendationType.PROPERTY : ApplicationMultiLeadRecommendationType.OFFER;

            hitMultileadRecommendationShown({
                recommendationType: isAbroadLead ? recommendationsType : ApplicationMultiLeadRecommendationType.VENDOR,
                recommendationId: null,
                viewType: currentViewType,
                recommendationSource: recommendationSource,
                multileadVersion:
                    recommendationsType === ApplicationMultiLeadRecommendationType.PROPERTY ? MultileadVersion.RP_LIST_PROPERTY : MultileadVersion.RP_LIST_OFFER
            });
        }

        const originalApplication = sentApplication ? sentApplication : null;
        const isWithOriginalApplicationResponse = Boolean(originalApplication);
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;
        const isWithRecommendations = recommendedList && recommendedList.length > 0;

        if (isMultileadStepEnabled && isWithOriginalApplicationResponse && isWithRecommendations) {
            setMultiLeadStep({
                originalApplicationResponse: originalApplication,
                recommendedOffers: recommendedList,
                shouldCloseOnOverlayClick: false,
                multiLeadProperty,
                recommendationsWithPriceFilter
            });
            return;
        }

        const isMarketingOptInSelected = sentApplication?.marketing_email || sentApplication?.marketing_sms;
        if (isWithOriginalApplicationResponse && !isMarketingOptInSelected) {
            setNotificationConsentStep({
                originalApplicationResponse: originalApplication
            });
            return;
        }

        setModalClose();
        return;
    };

    // Render
    const renderContent = () => {
        // render cases
        switch (modalStepState.step) {
            case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
                return initialLeadFormValues ? (
                    <LeadModalUnifiedForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                ) : null;
            }
            case ApplicationModalStep.APPLICATION_FORM_FULL_OFFER: {
                return (
                    <LeadModalAskForFullOfferForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return (
                    <LeadModalNegotiationForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                        price={propertyPrice}
                    />
                );
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return (
                    <LeadModalTopicForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case ApplicationModalStep.NOTIFICATION_CONSENT: {
                return (
                    <ApplicationNotificationConsentConfirmationModal
                        storeHash={parametrizedStoreHash}
                        result={modalStepState.originalApplicationResponse}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        onModalClose={onModalClose}
                        sourceSection={sourceSection}
                    />
                );
            }
            case ApplicationModalStep.SUCCESS_INFO: {
                return (
                    <ApplicationSuccessInfoModal
                        isModal
                        modalStepState={modalStepState}
                        onClick={() =>
                            dispatchModalStepState({
                                type: ModalStepActionTypes.NEXT_STEP
                            })
                        }
                        shouldRenderRecommendations
                        onModalClose={onModalClose}
                    />
                );
            }
            case ApplicationModalStep.GOODBYE_INFO: {
                return (
                    <ApplicationGoodbyeContentModal storeHash={parametrizedStoreHash} source={source} sourceSection={sourceSection} closeModal={onModalClose} />
                );
            }
            case ApplicationModalStep.MULTILEAD: {
                if ((!isMultileadStepEnabled && initialStep === ApplicationModalStep.MULTILEAD) || areRecommendationsLoading) {
                    return (
                        <div className={multiLeadWrapperStyle}>
                            <CenterBox>
                                <Loader />
                            </CenterBox>
                        </div>
                    );
                }
                if (!isMultileadStepEnabled) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return null;
                }

                const sentLeadFormValues = getSentLeadFormValuesFromStorage();
                if (!sentLeadFormValues && !preventClosingWhenNoData) {
                    // application data should be filled at this point. But just in case:
                    onModalClose();
                    return null;
                }

                const multiLeadOriginalApplication = modalStepState.originalApplicationResponse || originalApplication || null;

                if (areRecommendationsEmpty) {
                    setPreferencesStepWithCountryCheck({
                        originalApplicationResponse: multiLeadOriginalApplication,
                        multiLeadSummaryItems: [],
                        propertyId,
                        offerId
                    });
                }

                const originalApplicationFormValues = sentLeadFormValues
                    ? {...sentLeadFormValues, phone: getPhoneDataFromString(sentLeadFormValues.phone)}
                    : {...initialEmptyApplicationValues, rooms: [roomCount]};

                return (
                    <div className={multiLeadWrapperStyle}>
                        <ApplicationMultiLead
                            offerId={offerId || undefined}
                            propertyId={propertyId || undefined}
                            originalApplicationResponse={multiLeadOriginalApplication}
                            originalApplicationFormValues={originalApplicationFormValues}
                            storeHash={parametrizedStoreHash}
                            sourceSection={ApplicationSourceSection.MULTILEAD}
                            recommendationType={recommendationType}
                            recommendationsWithPriceFilter={modalStepState.recommendationsWithPriceFilter}
                            customSourceSection={customSourceSection || undefined}
                            handleModalClose={handleCloseMultiLead}
                            handleSetMultiLeadSummaryStep={setAfterMultiLeadStep}
                        />
                    </div>
                );
            }
            case ApplicationModalStep.WAITING:
            default:
                return (
                    <div className={waitingWrapperStyle}>
                        <Loader size="lg" fullWidth />
                    </div>
                );
        }
    };

    const shouldCloseOnOverlayClick = modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ? false : modalStepState.shouldCloseOnOverlayClick;

    return (
        <Modal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            zIndex={leadModalZindex}
            showCloseButton={false}
            fullScreen="xs"
        >
            {modalStepState.step === ApplicationModalStep.APPLICATION_FORM_STANDARD ? (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderStyle}>{renderContent()}</div>
                </div>
            ) : (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderFullStyle}>{renderContent()}</div>
                </div>
            )}
        </Modal>
    );
};

const applicationWrapStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%"
});

const applicationHolderStyle = css({
    width: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const applicationHolderFullStyle = css({
    width: "100%",
    height: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const multiLeadWrapperStyle = css({
    height: "100%",
    md: {
        width: "950px",
        height: "auto"
    }
});

const waitingWrapperStyle = css({
    position: "relative",
    md: {
        width: "800px",
        height: "auto"
    }
});
